import { useEffect, useState } from 'react';
import { getUserProfile } from './utils/auth';
import CircularProgress from '@mui/material/CircularProgress';
import { Box, Typography, Button, Paper } from '@mui/material';
import { FaExclamationCircle } from 'react-icons/fa';
import Router from "./routes";
import backgroundImage from './background.png';
import DevModeCredentialsModal from './components/DevModeCredentialsModal';
import { useCredentials } from './contexts/CredentialsContext';
import { getConfig } from './config';

function AppContent() {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [showCredentialsModal, setShowCredentialsModal] = useState(false);
  const { setCredentials } = useCredentials();
  const config = getConfig();

  const handleCredentialsSubmit = async (apiKey) => {
    if (!apiKey) {
      setErrorMessage('API Key is required');
      return;
    }

    setIsLoading(true);
    setErrorMessage('');

    try {
      // Validate API key by calling health check endpoint
      const healthCheckUrl = `${config.apiUrl}/api/health?api-key=${encodeURIComponent(apiKey)}&mode=${config.mode}`;

      const response = await fetch(healthCheckUrl, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        },
        // Include credentials (cookies) in production for JWT authentication
        credentials: config.mode === 'prod' ? 'include' : 'omit'
      });

      if (response.ok) {
        // API key is valid, store it and proceed
        setCredentials(apiKey);
        setShowCredentialsModal(false);

        if (config.mode === 'dev') {
          setIsLoggedIn(true);
        } else {
          userLogin();
        }
      } else if (response.status === 401 || response.status === 403) {
        // Invalid API key
        setErrorMessage('Invalid API key. Please check your credentials and try again.');
      } else {
        // Other error
        setErrorMessage('Failed to validate API key. Please try again.');
      }
    } catch (error) {
      console.error('Health check error:', error);
      setErrorMessage('Network error. Please check your connection and try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const userLogin = async () => {
    setErrorMessage('');
    setIsLoading(true);
    setIsLoggedIn(false);

    try {
      const loginResult = await getUserProfile();
      console.log("profile: ", loginResult);

      if (loginResult && !loginResult["groups"].includes("TEST_DATA_FAQ")) {
        throw new Error("You are not authorized to use this chat. Please contact admin.")
      }

      setIsLoggedIn(true);
    } catch (err) {
      console.error(err);
      setIsLoggedIn(false);
      setErrorMessage(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const title = config.mode === 'dev' ? 'TDS Onboarding - Dev' : 'TDS Onboarding WebApp';
    document.title = title;

    if (config.mode === 'dev') {
      // Show modal in dev mode on page load (no persistence)
      setShowCredentialsModal(true);
      return;
    }

    // Production mode: use regular authentication
    userLogin();
  }, []);

  return (
    <>
      {/* Dev Mode Credentials Modal */}
      {config.mode === 'dev' && (
        <DevModeCredentialsModal
          open={showCredentialsModal}
          onSubmit={handleCredentialsSubmit}
          loading={isLoading}
          externalError={errorMessage}
        />
      )}

      {isLoading ? (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '100vh',
            position: 'relative',
            padding: 3,
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundImage: `url(${backgroundImage})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              backgroundRepeat: 'no-repeat',
              filter: 'blur(10px)',
              zIndex: 0
            }
          }}
        >
          <CircularProgress 
            size={56} 
            thickness={4}
            sx={{ 
              color: '#FFCC00',
              mb: 3,
              position: 'relative',
              zIndex: 1
            }} 
          />
          <Typography 
            variant="h6" 
            sx={{ 
              color: '#ffffff',
              fontWeight: 500,
              fontSize: { xs: '1rem', sm: '1.125rem' },
              position: 'relative',
              zIndex: 1
            }}
          >
            Checking login status...
          </Typography>
        </Box>
      ) : isLoggedIn ? (
        <Box sx={{ marginTop: '40px' }}>
          <Router />
        </Box>
      ) : (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '100vh',
            padding: { xs: 2, sm: 3, md: 4 },
            position: 'relative',
            overflow: 'hidden',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundImage: `url(${backgroundImage})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              backgroundRepeat: 'no-repeat',
              filter: 'blur(8px)',
              zIndex: 0
            }
          }}
        >
          <Paper
            elevation={0}
            sx={{
              maxWidth: 420,
              width: '100%',
              padding: { xs: 3, sm: 3.5, md: 4 },
              borderRadius: 3,
              textAlign: 'center',
              animation: 'fadeInUp 0.6s ease-out',
              backgroundColor: '#ffffff',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)',
              position: 'relative',
              zIndex: 1
            }}
          >
            <Box sx={{ mb: 2.5 }}>
              <Box
                sx={{
                  width: 70,
                  height: 70,
                  margin: '0 auto 16px',
                  borderRadius: '12px',
                  background: '#ffffff',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: '0 8px 24px rgba(0, 48, 135, 0.2)',
                  position: 'relative',
                  padding: 1.5,
                  border: '2px solid #f0f0f0'
                }}
              >
                <img
                  src="/logo.ico"
                  alt="Logo"
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'contain'
                  }}
                />
              </Box>

              <Typography
                variant="h4"
                component="h1"
                sx={{
                  fontWeight: 700,
                  color: '#003087',
                  mb: 0.5,
                  letterSpacing: '-0.01em',
                  fontSize: { xs: '1.25rem', sm: '1.5rem', md: '1.625rem' }
                }}
              >
                TDS Onboarding Service
              </Typography>

              <Typography
                variant="body1"
                sx={{
                  color: '#666666',
                  fontSize: '0.875rem',
                  lineHeight: 1.5,
                  fontWeight: 400
                }}
              >
                Please sign in to continue
              </Typography>
            </Box>

            {errorMessage && (
              <Box
                sx={{
                  mb: 2,
                  p: 1.5,
                  borderRadius: 2,
                  backgroundColor: '#FEF2F2',
                  border: '1px solid #FCA5A5',
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 1.5,
                  animation: 'fadeIn 0.3s ease-out'
                }}
              >
                <FaExclamationCircle
                  style={{
                    color: '#DC2626',
                    marginTop: 2,
                    fontSize: 20,
                    flexShrink: 0
                  }}
                />
                <Typography
                  variant="body1"
                  sx={{
                    textAlign: 'left',
                    flex: 1,
                    color: '#991B1B',
                    fontSize: '0.875rem',
                    lineHeight: 1.5,
                    fontWeight: 500
                  }}
                >
                  {errorMessage}
                </Typography>
              </Box>
            )}

            <Button
              variant="contained"
              size="large"
              fullWidth
              href="https://auth.api.celcomdigi.tech/login/sp_auth?app=tds-onboard-prod"
              rel="noopener noreferrer"
              sx={{
                height: 44,
                fontSize: '1rem',
                fontWeight: 600,
                borderRadius: 2,
                textTransform: 'none',
                background: 'linear-gradient(135deg, #FFCC00 0%, #FFB800 100%)',
                color: '#003087',
                boxShadow: '0 4px 12px rgba(255, 204, 0, 0.4)',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                letterSpacing: '0.01em',
                '&:hover': {
                  background: 'linear-gradient(135deg, #FFD633 0%, #FFCC00 100%)',
                  boxShadow: '0 6px 16px rgba(255, 204, 0, 0.5)',
                  transform: 'translateY(-2px)'
                },
                '&:active': {
                  transform: 'translateY(0)',
                  boxShadow: '0 2px 8px rgba(255, 204, 0, 0.4)'
                }
              }}
            >
              Sign in
            </Button>

            {errorMessage && (
              <Box
                sx={{
                  mt: 2.5,
                  pt: 2,
                  borderTop: '1px solid #e5e5e5'
                }}
              >
                <Typography
                  variant="body1"
                  display="block"
                  sx={{
                    mb: 1.5,
                    fontWeight: 600,
                    color: '#003087',
                    fontSize: '0.875rem'
                  }}
                >
                  Need assistance? Contact us now!
                </Typography>

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                  {[
                    { name: 'Martin Thong', email: 'chenghow@celcomdigi.com' },
                    { name: 'Hanie', email: 'nhanieq.mjamil@celcomdigi.com' },
                    { name: 'Rachelle Foong', email: 'chyeching.foong@celcomdigi.com' }
                  ].map((contact, index) => (
                    <Typography
                      key={index}
                      variant="body1"
                      sx={{
                        fontSize: '0.8125rem',
                        color: '#666666',
                        '& a': {
                          color: '#003087',
                          textDecoration: 'none',
                          fontWeight: 500,
                          transition: 'color 0.2s ease',
                          '&:hover': {
                            color: '#001F5C',
                            textDecoration: 'underline'
                          }
                        }
                      }}
                    >
                      <a href={`mailto:${contact.email}`}>{contact.email}</a>
                    </Typography>
                  ))}
                </Box>
              </Box>
            )}

            <Box sx={{ mt: 3 }}>
              <Typography
                variant="caption"
                sx={{
                  color: '#999999',
                  fontSize: '0.75rem',
                  display: 'block',
                  textAlign: 'center'
                }}
              >
                CelcomDigi E2E Automation Team
              </Typography>
            </Box>
          </Paper>
        </Box>
      )}
    </>
  );
}

export default AppContent;
